/*
  ============================================================================
  bb-fit-verdict.js  —  the product page fitment verdict
  ----------------------------------------------------------------------------
  Owner:    Linea Group (lineagroup.ae) for BlackBoost USA
  Store:    blackboost-usa.myshopify.com  —  theme "Linea DEV - do not publish"
  Created:  2026-08-12

  WHAT THIS IS
  The renderer for snippets/bb-fit-verdict.liquid, the block that sits directly
  above the product title. It is a renderer and nothing else.

  IT DECIDES NOTHING
  Every verdict comes from BBGarage.verdict(handle) in assets/bb-garage.js.
  This file never calls fits(), never reads the products map, never looks at
  dataset.verified. If it did, there would be two places that can turn a match
  green and only one of them would be gated. There is one gate and it is in
  bb-garage.js. All this file does is choose the markup for a state it was
  handed, and phrase it for a page where the shopper is one tap from buying.

  WHY IT EXISTS AT ALL, GIVEN [data-bb-fit] ALREADY RENDERS
  The generic renderer in bb-garage.js paints a card or a list row. The product
  page needs four things it does not do:
    1. the shopper's car echoed back in their own words, in the headline
    2. a compact "Will this fit your car?" prompt that is server-rendered, so
       the first-time shopper sees no shift and no flash
    3. a red state that always leads somewhere: the parts that DO fit
    4. a live region, so the verdict is announced when it changes
  So the host carries data-bb-fit-manual, the generic renderer skips it, and
  this file owns that one block.

  THE FOUR STATES, AS PAINTED HERE
    amber       a match, while the data is unverified. Pill + "Listed by
                BlackBoost for your <car>" + the confirm-before-you-order
                promise + a contact link.
    red         fitment rows exist and none of them is the shopper's car.
                Pill + "This does not fit your <car>" + a link to the parts
                that do. Never a dead end.
    neutral     no fitment rows at all (41 of 132 products: filters,
                intercoolers, apparel, universal kit). A quiet line and an
                ask-us link. No pill, no colour, no border. Not a grey
                "unknown", and never red.
    green       built, and unreachable until bb-fitment.json says
                "verified": true. bb-garage.js owns that gate.
  Plus two non-verdicts: 'unset' (no car chosen: the server-rendered prompt is
  already correct, so nothing is touched) and 'unavailable' (dataset failed to
  load: say so, point at the description, offer the contact link).

  COPY
  Pills and the shared action labels are read off BBGarage.copy so the two
  renderers cannot drift apart. The lead sentences below belong to this file:
  the product page puts the shopper's actual car where the generic copy says
  "your car", which a collection card cannot do. Override any of it with
  window.BBFitVerdictConfig.copy before this file loads. Locale files are not
  editable on this theme, so the strings are English literals, same as the core.

  Dependencies: assets/bb-garage.js (loaded by snippets/bb-garage-bar.liquid).
  No jQuery, no CDN, no build step.
  ============================================================================
*/

(function () {
  'use strict';

  if (window.BBFitVerdict && window.BBFitVerdict.__isBBFitVerdict) return;

  /* ------------------------------------------------------------------ config */

  var BLOCK_SELECTOR = '[data-bb-fit-block]';
  var GARAGE_WAIT_MS = 6000; // how long to wait for bb-garage.js before giving up
  var GARAGE_POLL_MS = 150;
  var DATA_WAIT_MS = 12000; // how long an unresolved block waits before saying so

  var settings = window.BBFitVerdictConfig || {};

  var COPY = {
    // Lead lines. {vehicle} is the shopper's car in their own words.
    // Accorciata il 28 agosto 2026. Il blocco arrivava a 365px di altezza e
    // spingeva il nome del prodotto sotto la piega: il verdetto deve stare in
    // tre righe. La distinzione "listed" contro "confirmed" resta esplicita,
    // perche' e' l'unica cosa che ci protegge da un reso su un pezzo da 1.800.
    amberLead: 'Listed for your {vehicle}.',
    amberBody: 'We are confirming this exact fitment.',

    greenLead: 'Confirmed fit for your {vehicle}.',
    greenBody: '',

    redLead: 'This does not fit your {vehicle}.',
    redBody: 'Here is what we do list for it.',
    redBodyNone: 'Message us and we will tell you what we can do for your car.',

    neutralBody: 'This part is not listed against a specific vehicle.',
    neutralBodyVehicle: 'Not listed against a specific vehicle, so there is nothing to check against your {vehicle}.',

    unavailableBody:
      'We could not load the fitment list just now. The fitment for this part is ' +
      'written into the description below, or message us and we will confirm it by hand.',

    // Actions. Pills and two of these come off BBGarage.copy in buildCopy().
    amberAction: 'Message us about fitment',
    neutralAction: 'Ask us about this part',
    unlistedBody: 'We have not listed this part against a vehicle yet.',
    unlistedBodyVehicle: 'We have not listed this part against a vehicle yet, so we cannot confirm it for your {vehicle}.',
    unavailableAction: 'Message us about fitment',
    redAction: 'See the {count} parts we list for your {model}',
    redActionOne: 'See the one part we list for your {model}',
    redActionNone: 'Browse the full catalogue',
    changeAction: 'Not your car? Change vehicle',

    amberPill: 'Check fitment',
    greenPill: 'Confirmed fit',
    redPill: 'Does not fit'
  };

  /* ------------------------------------------------------------------ helpers */

  function garage() {
    var g = window.BBGarage;
    return g && g.__isBBGarage ? g : null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function empty(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function fill(template, values) {
    var out = String(template == null ? '' : template);
    for (var key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
      out = out.split('{' + key + '}').join(values[key]);
    }
    return out;
  }

  // Pull the shared strings off the core so a wording change lands in one place,
  // then let window.BBFitVerdictConfig.copy win over everything.
  function buildCopy(g) {
    var shared = (g && g.copy) || {};
    var map = {
      amberPill: 'amberPill',
      greenPill: 'greenPill',
      redPill: 'redPill',
      amberAction: 'amberAction',
      neutralAction: 'neutralAction'
    };
    for (var key in map) {
      if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
      if (shared[map[key]]) COPY[key] = shared[map[key]];
    }
    COPY.unavailableAction = COPY.amberAction;
    if (settings.copy) {
      for (var own in settings.copy) {
        if (Object.prototype.hasOwnProperty.call(settings.copy, own)) COPY[own] = settings.copy[own];
      }
    }
  }

  /*
    The shopper's car in the shopper's own words:
      "2019-2021 C63 (C205/W205) M177"
      "2022-2024 BMW M240I (G42) B58"
    The make is dropped when the model already carries it (a Mercedes-AMG row
    whose model reads "AMG C63"), and kept when it does not, because "M340I"
    alone is thinner than "BMW M340I". Years come from BBGarage.yearLabel so the
    two files cannot format a range differently. Nothing here invents a value:
    if a platform has no model, the full dataset display string is used instead.
  */
  function shopperName(platform) {
    if (!platform) return '';
    var g = garage();
    var model = String(platform.model || '').trim();
    if (!model) return String(platform.display || platform.id || '');

    var bits = [];
    var years = g && g.yearLabel ? g.yearLabel(platform) : '';
    if (years) bits.push(years);

    var make = String(platform.make || '').trim();
    if (make && !modelCarriesMake(model, make)) bits.push(make);
    bits.push(model);

    // Nel dataset Instafit il modello porta gia' dentro telaio e motore
    // ("GLS63(S) AMG (X167) M177"), quindi riaggiungerli produce
    // "... (X167) M177 (X167) M177". Si aggiungono solo se mancano.
    var chassis = platform.chassis || [];
    var missing = [];
    for (var c = 0; c < chassis.length; c++) {
      if (!modelCarriesToken(model, chassis[c])) missing.push(chassis[c]);
    }
    if (missing.length) bits.push('(' + missing.join('/') + ')');
    if (platform.engine && !modelCarriesToken(model, platform.engine)) {
      bits.push(String(platform.engine));
    }

    return bits.join(' ');
  }

  function modelCarriesToken(model, token) {
    if (!token) return true;
    var hay = ' ' + String(model).toUpperCase().replace(/[^A-Z0-9]+/g, ' ') + ' ';
    var t = String(token).toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
    if (!t) return true;
    return hay.indexOf(' ' + t + ' ') !== -1;
  }

  function modelCarriesMake(model, make) {
    var haystack = ' ' + model.toUpperCase().replace(/[^A-Z0-9]+/g, ' ') + ' ';
    var tokens = make.toUpperCase().split(/[^A-Z0-9]+/);
    for (var i = 0; i < tokens.length; i++) {
      if (tokens[i].length < 2) continue;
      if (haystack.indexOf(' ' + tokens[i] + ' ') !== -1) return true;
    }
    return false;
  }

  // Short handle for a button label: "C63", not the whole platform string.
  function shortName(platform) {
    if (!platform) return 'car';
    var model = String(platform.model || '').trim();
    if (model) return model;
    return String(platform.display || 'car');
  }

  function contactUrl(host) {
    var g = garage();
    return (
      host.getAttribute('data-bb-contact-url') ||
      (g && g.config && g.config.contactUrl) ||
      '/pages/contact-us'
    );
  }

  // Where "see the parts that fit" goes. The core builds this when the block was
  // not given its own target, so the ?bb_v= param is written the same way twice.
  function browseUrl(host, platformId) {
    var g = garage();
    // Un solo posto costruisce questo indirizzo: BBGarage.browseHref, che dal
    // 7 set 2026 torna la pagina-tag dell'auto. Qui si costruiva a mano da
    // data-bb-browse-url, e usciva /collections/all?bb_v=..., cioe' il catalogo
    // intero con le etichette "DOES NOT FIT": il contrario di quello che chiede
    // il bottone "vedi i pezzi che vanno sulla mia auto".
    if (g && g.browseHref) return g.browseHref(platformId);
    var base = host.getAttribute('data-bb-browse-url') || '/collections/all';
    return base.replace(/\/$/, '') + '/fit-' + encodeURIComponent(platformId);
  }

  function fitCount(platformId) {
    var g = garage();
    if (!g || !g.productsFor) return 0;
    try {
      return g.productsFor(platformId).length;
    } catch (e) {
      return 0;
    }
  }

  /* ------------------------------------------------------------------- render */

  function blocks(scope) {
    return (scope || document).querySelectorAll(BLOCK_SELECTOR);
  }

  function renderAll(scope) {
    var list = blocks(scope);
    for (var i = 0; i < list.length; i++) render(list[i]);
  }

  function render(host) {
    var g = garage();
    if (!g) return;

    var live = host.querySelector('[data-bb-fit-live]');
    if (!live) return;

    var handle = host.getAttribute('data-bb-fit') || '';
    var result = g.verdict(handle);
    var state = result.state;
    var vehicle = g.get();

    if (state === 'idle') {
      // Dataset not in yet. Either it failed, and we say so, or it is still on
      // its way and the server-rendered shell stays exactly as it is. Painting
      // anything else here is how a wrong verdict flashes on screen.
      if (document.documentElement.getAttribute('data-bb-fitment') === 'unavailable') {
        state = 'unavailable';
      } else {
        host.setAttribute('data-bb-fit-state', 'idle');
        return;
      }
    }

    // Re-rendering identical content would announce it to a screen reader again.
    var signature = [state, vehicle ? vehicle.id : '-', handle, g.isVerified() ? 'v' : 'u'].join('|');
    if (host.getAttribute('data-bb-fit-sig') === signature) return;
    host.setAttribute('data-bb-fit-sig', signature);
    host.setAttribute('data-bb-fit-state', state);

    /*
      'unlisted' = questo pezzo non ha nessuna lista auto, da nessuna fonte.
      Sono 7 schede su 134: i due pezzi di trasmissione, la felpa, i tre bundle e
      le personalizzazioni. Dal 7 set 2026 non si scrive piu' niente: la frase
      "non l'abbiamo ancora abbinato alla tua auto" su una felpa non informa,
      confonde, e su un bundle da 3.400 dollari fa dubitare. Sono fuori da tutto
      cio' che e' filtrato per auto (il filtro della collection li nasconde, le
      pagine-tag non li contengono perche' non hanno il tag fit-), e restano
      raggiungibili dalla home, dalle categorie e dalla ricerca.
      Quando Chris confermera' le loro auto torneranno verdi o rossi da soli.

      1 ott 2026: "non scrivere niente" non bastava. Questo ramo era copiato da
      'unset' e lasciava su il guscio stampato dal server: con un'auto scelta
      "Checking this part against your <auto>" per sempre, senza auto "Will this
      fit your car?" anche sulla felpa. Ora il blocco si chiude del tutto.
    */
    if (state === 'unlisted') {
      empty(live);
      host.setAttribute('data-bb-resolved', 'true');
      host.hidden = true;
      return;
    }
    // Un blocco chiuso da 'unlisted' si riapre se lo stato cambia (correzioni).
    if (host.hidden) host.hidden = false;

    if (state === 'unset') {
      // The server already rendered this exact prompt. Leave it: no DOM churn,
      // no layout shift, no announcement of something that did not change.
      host.removeAttribute('data-bb-resolved');
      empty(live);
      return;
    }

    var card = buildCard(state, vehicle, host);
    empty(live);
    live.appendChild(card);
    host.setAttribute('data-bb-resolved', 'true');
  }

  function buildCard(state, vehicle, host) {
    var name = shopperName(vehicle);
    var card;

    // 'unlisted' e' il caso nuovo: la compatibilita' esiste, non l'abbiamo
    // ancora scritta. Si mostra come il neutro, riga sobria e link per chiedere,
    // ma con una frase che non promette niente. La differenza vera sta nella
    // collection, dove il filtro lo nasconde invece di lasciarlo passare per
    // universale: e' li' che una BMW finiva sotto gli occhi di chi ha una AMG.
    if (state === 'neutral' || state === 'unavailable' || state === 'unlisted') {
      /*
        A quiet line and nothing else. No pill, no colour, no box, no buttons.
        A universal part must not look like it was judged, and a $3,990
        intercooler must never look red. The ask-us link sits inside the
        sentence rather than under it: two stacked 44px buttons turn "there is
        nothing to check here" into the loudest thing above the product title.
      */
      card = el('div', 'bb-fit__note');
      var noteText =
        state === 'unavailable'
          ? COPY.unavailableBody
          : state === 'unlisted'
          ? (vehicle ? fill(COPY.unlistedBodyVehicle, { vehicle: name }) : COPY.unlistedBody)
          : vehicle
          ? fill(COPY.neutralBodyVehicle, { vehicle: name })
          : COPY.neutralBody;

      var note = el('p', 'bb-fit__note-text', noteText + ' ');
      var ask = el('a', 'bb-fit__note-link', state === 'unavailable' ? COPY.unavailableAction : COPY.neutralAction);
      ask.href = contactUrl(host);
      note.appendChild(ask);
      card.appendChild(note);
      return card;
    }

    card = el('div', 'bb-verdict bb-verdict--' + state + ' bb-fit__card');

    var pill = COPY[state + 'Pill'];
    if (pill) card.appendChild(el('span', 'bb-pill bb-pill--' + state, pill));

    var lead = COPY[state + 'Lead'];
    if (lead) card.appendChild(el('p', 'bb-fit__headline', fill(lead, { vehicle: name })));

    var actions = el('div', 'bb-fit__actions');

    if (state === 'amber') {
      if (COPY.amberBody) card.appendChild(el('p', 'bb-verdict__body', COPY.amberBody));
      actions.appendChild(link(COPY.amberAction, contactUrl(host), 'primary'));
    } else if (state === 'green') {
      if (COPY.greenBody) card.appendChild(el('p', 'bb-verdict__body', fill(COPY.greenBody, { vehicle: name })));
    } else if (state === 'red') {
      var count = vehicle ? fitCount(vehicle.id) : 0;
      var model = shortName(vehicle);
      if (count > 0) {
        if (COPY.redBody) card.appendChild(el('p', 'bb-verdict__body', COPY.redBody));
        var label = count === 1 ? COPY.redActionOne : COPY.redAction;
        actions.appendChild(
          link(fill(label, { count: count, model: model }), browseUrl(host, vehicle.id), 'primary')
        );
      } else {
        // Nothing in the dataset fits this car. Sending the shopper to a filtered
        // view that is empty is worse than sending them nowhere, so send them to
        // the unfiltered catalogue and to us.
        if (COPY.redBodyNone) card.appendChild(el('p', 'bb-verdict__body', COPY.redBodyNone));
        actions.appendChild(link(COPY.redActionNone, unfilteredBrowseUrl(host), 'primary'));
        actions.appendChild(link(COPY.amberAction, contactUrl(host), 'link'));
      }
    }

    if (vehicle) actions.appendChild(changeButton());
    if (actions.childNodes.length) card.appendChild(actions);
    return card;
  }

  function unfilteredBrowseUrl(host) {
    var g = garage();
    return host.getAttribute('data-bb-browse-url') || (g && g.config && g.config.browseUrl) || '/collections/all';
  }

  function link(label, href, kind) {
    var node = el('a', 'bb-btn bb-btn--' + kind + ' bb-fit__action', label);
    node.href = href;
    return node;
  }

  // [data-bb-open-picker] is the core's own delegated hook. Nothing to wire up.
  function changeButton() {
    var node = el('button', 'bb-btn bb-btn--link bb-fit__change', COPY.changeAction);
    node.type = 'button';
    node.setAttribute('data-bb-open-picker', '');
    return node;
  }

  /*
    Falls back to the no-JS line: the fitment is in the description, which is
    true on every product on this store. Used when bb-garage.js never arrives,
    which means the picker does not exist either and the prompt button would be
    a dead control.
  */
  function fallbackAll(reason) {
    var list = blocks();
    for (var i = 0; i < list.length; i++) {
      list[i].classList.add('bb-fit--no-garage');
      list[i].setAttribute('data-bb-fit-state', 'idle');
    }
    if (list.length && window.console) {
      console.warn(
        'bb-fit-verdict: ' +
          reason +
          '. Render bb-garage-bar once per page (it loads bb-garage.js), see bb-garage-INTEGRATION.md.'
      );
    }
  }

  /* -------------------------------------------------------------------- watch */

  // Dawn swaps parts of the product info column on variant change, and quick-add
  // injects a whole product form. Pick up any block that arrives that way.
  function watchDom() {
    if (!window.MutationObserver) return;
    var queued = [];
    var scheduled = false;

    function flushQueue() {
      scheduled = false;
      var nodes = queued;
      queued = [];
      for (var i = 0; i < nodes.length; i++) {
        if (!nodes[i].isConnected) continue;
        if (nodes[i].matches && nodes[i].matches(BLOCK_SELECTOR)) render(nodes[i]);
        renderAll(nodes[i]);
      }
    }

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches(BLOCK_SELECTOR)) ||
            (node.querySelector && node.querySelector(BLOCK_SELECTOR))
          ) {
            queued.push(node);
          }
        }
      }
      if (queued.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flushQueue);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------- bootstrap */

  var api = {
    __isBBFitVerdict: true,
    VERSION: '1.0.0',
    // Guarded: this gets handed around as a callback, and a Promise or an Event
    // arriving where a DOM scope is expected would throw inside querySelectorAll.
    refresh: function (scope) {
      renderAll(scope && scope.querySelectorAll ? scope : document);
    },
    shopperName: shopperName,
    copy: COPY
  };
  window.BBFitVerdict = api;

  function start(g) {
    buildCopy(g);

    // Fires immediately with the current vehicle, and again on every change,
    // including the change that lands when the dataset finishes loading.
    g.subscribe(function () {
      renderAll();
    });

    // The dataset can fail after boot. verdict() then reports 'idle' forever and
    // nothing else would ever call us back.
    document.addEventListener('bb:fitment-error', function () {
      renderAll();
    });
    if (g.ready) {
      g.ready().then(
        function () {
          renderAll();
        },
        function () {
          renderAll();
        }
      );
    }

    watchDom();

    // A fetch that neither resolves nor rejects would leave the shell up for
    // good. Say the list is unavailable instead of spinning silently.
    window.setTimeout(function () {
      var list = blocks();
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute('data-bb-fit-state') === 'idle' && !g.data) {
          document.documentElement.setAttribute('data-bb-fitment', 'unavailable');
          renderAll();
          return;
        }
      }
    }, DATA_WAIT_MS);
  }

  function boot() {
    var g = garage();
    if (g) {
      start(g);
      return;
    }
    // bb-garage.js is loaded by bb-garage-bar.liquid, which sits at the top of
    // <main>, so it normally exists before this file runs. Wait, do not assume.
    var waited = 0;
    var timer = window.setInterval(function () {
      var found = garage();
      if (found) {
        window.clearInterval(timer);
        start(found);
        return;
      }
      waited += GARAGE_POLL_MS;
      if (waited >= GARAGE_WAIT_MS) {
        window.clearInterval(timer);
        fallbackAll('BBGarage never loaded');
      }
    }, GARAGE_POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
